import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { defaultCTAURI } from 'src/modules/utils/constants'
import { useSelector } from 'react-redux'
import { selectCTAParams } from 'src/modules/reducers/params'
import { createUrl } from 'src/modules/utils/createUrl'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import { fireUIInteractionEvent, fireUIRenderEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { selectAnalyticsReady } from 'src/modules/reducers/analytics'
import { getTextContent } from 'src/modules/utils/text'

// Design System imports
import {
  colors,
  spacing,
  heading,
  typographyMixin,
  mediaQuery,
  maxWidth,
} from 'src/constants/marketing-ds'
import { PromoBadge } from 'src/components/icons/PromoBadge'
const defaultTitle = {
  en: ['Special Offer Applied'],
  es: ['Oferta Especial Aplicada'],
}

export const OfferMessage = ({ title, showIcon = true }) => {
  if (!title || !Array.isArray(title) || title.length === 0) return null

  return (
    <PromoBadgeWrapper>
      {title.map((line, index) => (
        <React.Fragment key={index}>
          {index === 0 ? (
            <span className="promo-badge">
              {showIcon && <PromoBadge />} {line}
            </span>
          ) : (
            <span>{line}</span>
          )}
        </React.Fragment>
      ))}
    </PromoBadgeWrapper>
  )
}

const Banner = ({ details, ctaHrefOverride, lang = 'en' }) => {
  const { title, showIcon = true } = details
  const parsedParams = useSelector(selectCTAParams)
  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const [isPromoBannerRendered, setIsPromoBannerRendered] = useState(false)
  const isAnalyticsReady = useSelector(selectAnalyticsReady)
  let ctaURL = createUrl(defaultCTAURI, parsedParams)
  ctaURL = replaceUrlParam(ctaURL, 'comp', 'offerbanner')
  const ctaLink = ctaHrefOverride || ctaURL
  const plainTextTitle = getTextContent(title || defaultTitle[lang])

  const handleOnClick = () => {
    const isAnchor = ctaLink.startsWith('#')

    fireUIInteractionEvent(
      {
        element: plainTextTitle,
        component: 'offer_banner_clicked',
        type: isAnchor ? 'clicked_anchor' : 'clicked',
      },
      stateForProtobuf
    )
  }

  useEffect(() => {
    if (!isAnalyticsReady) return
    if (plainTextTitle && !isPromoBannerRendered) {
      fireUIRenderEvent(
        {
          element: plainTextTitle,
          component: 'promo_banner',
        },
        stateForProtobuf
      )
      setIsPromoBannerRendered(true)
    }
  }, [isAnalyticsReady, isPromoBannerRendered, plainTextTitle])

  return (
    <BannerWrapper onClick={handleOnClick} href={ctaLink} className="offer-banner">
      <BannerInner>
        <BannerTitle className="banner-title">
          <OfferMessage title={title} showIcon={showIcon} />
        </BannerTitle>
      </BannerInner>
    </BannerWrapper>
  )
}

export default Banner

const BannerWrapper = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${colors.semantic.info};
  position: sticky;
  color: ${colors.semantic.text.primary};
  top: 0;
  z-index: 10000;
  cursor: ${(props) => (props.onClick ? 'pointer' : 'auto')};
  text-decoration: none;
  width: 100vw;
  max-width: 100%;
  box-sizing: border-box;

  padding: ${spacing.md} ${spacing.xs};

  ${mediaQuery.tablet} {
    padding: ${spacing.md} ${spacing.lg};
  }

  ${mediaQuery.desktop} {
    padding: ${spacing.md} ${spacing.xl};
  }

  &:hover {
    text-decoration: none;
  }
`

const BannerInner = styled.div`
  position: relative;
  width: 100%;
  max-width: ${maxWidth.desktop};
`

const BannerTitle = styled.div`
  ${typographyMixin(heading.xs)}
  color: ${colors.semantic.text.primary};
  text-align: center;

  ${mediaQuery.tablet} {
    ${typographyMixin(heading.sm)}
  }

  ${mediaQuery.desktop} {
    ${typographyMixin(heading.md)}
  }
`

const PromoBadgeWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  flex-direction: column;
  gap: ${spacing.xxxxs};

  ${mediaQuery.tablet} {
    flex-direction: row;
  }

  .promo-badge {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: ${spacing.xxxs}; // Add consistent gap between badge and text

    .promo-badge-icon {
      width: 18px;
      height: 18px;

      ${mediaQuery.tablet} {
        width: 25px;
        height: 25px;
        margin-top: 2px;
      }
    }
  }
`
